import React, { useEffect, useState } from 'react';
import { buildReportPdf } from '../../components/orders/reportPdf';
import { orderService } from '../../services/api';
import { Order } from '../../types/order';
import styles from './Relatorios.module.css';

const parseInputDateAsLocal = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const parseApiDate = (value: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)) {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, secondsWithMs] = timePart.split(':');
    const [seconds, milliseconds = '0'] = secondsWithMs.split('.');
    return new Date(
      year,
      month - 1,
      day,
      Number(hours),
      Number(minutes),
      Number(seconds),
      Number(milliseconds.padEnd(3, '0').slice(0, 3)),
    );
  }
  return new Date(value);
};

const formatInputDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date: Date): string => date.toLocaleDateString('pt-BR');
const formatDateTime = (value: string): string => {
  const d = new Date(value);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const applyPresetRange = (preset: '7d' | '14d' | '30d' | 'currentMonth'): { start: string; end: string } => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);

  if (preset === 'currentMonth') {
    start.setDate(1);
  } else if (preset === '30d') {
    start.setDate(today.getDate() - 29);
  } else if (preset === '14d') {
    start.setDate(today.getDate() - 13);
  } else {
    start.setDate(today.getDate() - 6);
  }

  return {
    start: formatInputDate(start),
    end: formatInputDate(today),
  };
};

const InfoIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={styles.infoIconSvg}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const CardInfo: React.FC<{ text: string }> = ({ text }) => (
  <span className={styles.infoWrap}>
    <InfoIcon />
    <span className={styles.tooltip} role="tooltip">{text}</span>
  </span>
);

const Relatorios: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [selectedPreset, setSelectedPreset] = useState<'7d' | '14d' | '30d' | 'currentMonth' | null>('14d');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('');

  useEffect(() => {
    const params: Record<string, string> = {};
    if (reportStart) params.start = reportStart;
    if (reportEnd) params.end = reportEnd;
    orderService.getAllPages(params)
      .then((ordersResponse) => {
        setOrders(ordersResponse);
        setFetchError('');
      })
      .catch(() => {
        setFetchError('Erro ao carregar dados do servidor. Verifique sua conexao.');
      })
      .finally(() => setLoading(false));
  }, [reportStart, reportEnd]);

  useEffect(() => {
    setCurrentPage(1);
  }, [reportStart, reportEnd]);

  useEffect(() => {
    const defaultRange = applyPresetRange('14d');
    setReportStart(defaultRange.start);
    setReportEnd(defaultRange.end);
  }, []);

  // ── Derived report data ────────────────────────────────────────────────────
  const periodStart = reportStart ? parseInputDateAsLocal(reportStart) : null;
  const periodEnd = reportEnd ? parseInputDateAsLocal(reportEnd) : null;

  if (periodEnd) {
    periodEnd.setHours(23, 59, 59, 999);
  }

  const periodDays = periodStart && periodEnd
    ? Math.max(1, Math.ceil((periodEnd.getTime() - periodStart.getTime() + 1) / (1000 * 60 * 60 * 24)))
    : 0;

  const ordersInPeriod = periodStart && periodEnd
    ? orders.filter((o) => {
        const updated = parseApiDate(o.updatedAt);
        return updated >= periodStart && updated <= periodEnd;
      })
    : [];

  const executedOrders = ordersInPeriod.filter((o) => o.status === 'CONCLUIDA');

  // ── Paginação da listagem ─────────────────────────────────────────────────
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.max(1, Math.ceil(executedOrders.length / ITEMS_PER_PAGE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * ITEMS_PER_PAGE;
  const paginatedExecutedOrders = executedOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Janela de páginas vizinhas (até 3 quadrados ao redor da página atual)
  const windowStart = Math.max(1, Math.min(safeCurrentPage - 1, totalPages - 2));
  const windowEnd = Math.min(totalPages, windowStart + 2);
  const visiblePages = Array.from(
    { length: Math.max(0, windowEnd - windowStart + 1) },
    (_, index) => windowStart + index,
  );

  const goToPage = () => {
    const target = parseInt(pageInput, 10);
    if (!Number.isNaN(target) && target >= 1 && target <= totalPages) {
      setCurrentPage(target);
    }
    setPageInput('');
  };

  const publicCount = ordersInPeriod.filter((o) => o.origin === 'PORTAL_PUBLICO').length;
  const celpeCount = ordersInPeriod.filter((o) => o.cadastroCelpe).length;
  const medidaCount = ordersInPeriod.filter((o) => o.medida).length;
  const estimadaCount = ordersInPeriod.filter((o) => o.estimada).length;

  const avgExecution = periodDays > 0 ? (executedOrders.length / periodDays).toFixed(2) : '0.00';

  // Aggregate materials
  const materialsMap: Record<string, number> = {};
  executedOrders.forEach((o) => {
    const cl = o.completionChecklist;
    if (!cl || !cl.materiaisUtilizados) return;
    cl.materiaisUtilizados.forEach((mat) => {
      const qty = cl.materiaisQtd?.[mat] ?? 1;
      materialsMap[mat] = (materialsMap[mat] ?? 0) + qty;
    });
  });

  const materialEntries = Object.entries(materialsMap).sort((a, b) => b[1] - a[1]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Relatórios</h1>
        <p className={styles.subtitle}>
          Relatório de execução de ordens de serviço em iluminação pública
        </p>
      </div>

      {fetchError && (
        <div className={styles.errorBanner}>
          <p>{fetchError}</p>
        </div>
      )}

      {/* Filtros */}
      <div className={styles.filterSection}>
        <div className={styles.presetButtons}>
          <button
            type="button"
            className={`${styles.presetButton} ${selectedPreset === '7d' ? styles.presetButtonActive : ''}`}
            onClick={() => {
              const range = applyPresetRange('7d');
              setSelectedPreset('7d');
              setReportStart(range.start);
              setReportEnd(range.end);
            }}
          >
            7 dias
          </button>
          <button
            type="button"
            className={`${styles.presetButton} ${selectedPreset === '14d' ? styles.presetButtonActive : ''}`}
            onClick={() => {
              const range = applyPresetRange('14d');
              setSelectedPreset('14d');
              setReportStart(range.start);
              setReportEnd(range.end);
            }}
          >
            14 dias
          </button>
          <button
            type="button"
            className={`${styles.presetButton} ${selectedPreset === '30d' ? styles.presetButtonActive : ''}`}
            onClick={() => {
              const range = applyPresetRange('30d');
              setSelectedPreset('30d');
              setReportStart(range.start);
              setReportEnd(range.end);
            }}
          >
            30 dias
          </button>
          <button
            type="button"
            className={`${styles.presetButton} ${selectedPreset === 'currentMonth' ? styles.presetButtonActive : ''}`}
            onClick={() => {
              const range = applyPresetRange('currentMonth');
              setSelectedPreset('currentMonth');
              setReportStart(range.start);
              setReportEnd(range.end);
            }}
          >
            Mês atual
          </button>
        </div>

        <div className={styles.dateFields}>
          <label className={styles.dateField}>
            Início
            <input
              type="date"
              className={styles.dateInput}
              value={reportStart}
              max={reportEnd || undefined}
              onChange={(event) => {
                setSelectedPreset(null);
                setReportStart(event.target.value);
              }}
            />
          </label>
          <label className={styles.dateField}>
            Fim
            <input
              type="date"
              className={styles.dateInput}
              value={reportEnd}
              min={reportStart || undefined}
              onChange={(event) => {
                setSelectedPreset(null);
                setReportEnd(event.target.value);
              }}
            />
          </label>
        </div>

        <div className={styles.generateSection}>
          <button
            type="button"
            className={styles.downloadButton}
            disabled={loading || !reportStart || !reportEnd}
            onClick={async () => {
              try {
                await buildReportPdf(orders, reportStart, reportEnd);
              } catch {
                alert('Erro ao gerar relatório em PDF');
              }
            }}
          >
            ⬇ Baixar PDF
          </button>
        </div>
      </div>

      {loading ? (
        <p className={styles.loadingText}>Carregando dados...</p>
      ) : (
        <>
          {/* Cards de resumo */}
          <div className={styles.cardsGrid}>
            <div className={`${styles.card} ${styles.cardBlue}`}>
              <span className={styles.cardLabel}>Período (dias) <CardInfo text="Número de dias do período selecionado (do início ao fim)." /></span>
              <span className={styles.cardValue}>{periodDays}</span>
            </div>
            <div className={`${styles.card} ${styles.cardGreen}`}>
              <span className={styles.cardLabel}>Ordens Executadas <CardInfo text="Ordens concluídas (status CONCLUÍDA) dentro do período selecionado." /></span>
              <span className={styles.cardValue}>{executedOrders.length}</span>
            </div>
            <div className={`${styles.card} ${styles.cardOrange}`}>
              <span className={styles.cardLabel}>Taxa Média (OS/dia) <CardInfo text="Média de ordens executadas por dia (ordens executadas ÷ dias do período)." /></span>
              <span className={styles.cardValue}>{avgExecution}</span>
            </div>
            <div className={`${styles.card} ${styles.cardPurple}`}>
              <span className={styles.cardLabel}>Público <CardInfo text="Ordens abertas pelo portal público (munícipe) no período." /></span>
              <span className={styles.cardValue}>{publicCount}</span>
            </div>
            <div className={`${styles.card} ${styles.cardBlue}`}>
              <span className={styles.cardLabel}>Cadastro Celpe <CardInfo text="Ordens marcadas com cadastro Celpe no período." /></span>
              <span className={styles.cardValue}>{celpeCount}</span>
            </div>
            <div className={`${styles.card} ${styles.cardGreen}`}>
              <span className={styles.cardLabel}>Medida <CardInfo text="Ordens marcadas como medida no período." /></span>
              <span className={styles.cardValue}>{medidaCount}</span>
            </div>
            <div className={`${styles.card} ${styles.cardOrange}`}>
              <span className={styles.cardLabel}>Estimada <CardInfo text="Ordens marcadas como estimada no período." /></span>
              <span className={styles.cardValue}>{estimadaCount}</span>
            </div>
          </div>

          {/* Materiais utilizados */}
          {materialEntries.length > 0 && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Materiais Utilizados no Período</h4>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th className={styles.qtyCol}>Qtd. Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialEntries.map(([mat, qty], idx) => (
                      <tr key={mat} className={idx % 2 === 0 ? styles.evenRow : styles.oddRow}>
                        <td>{mat}</td>
                        <td className={styles.qtyCol}>{qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Listagem de ordens executadas */}
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>
              Listagem de Ordens Executadas
              {periodStart && periodEnd && (
                <span className={styles.sectionSubtitle}>
                  {' '}
                  — {formatDisplayDate(periodStart)} a {formatDisplayDate(periodEnd)}
                </span>
              )}
            </h4>

            {executedOrders.length === 0 ? (
              <p className={styles.emptyText}>Nenhuma ordem concluída no período selecionado.</p>
            ) : (
              <div className={styles.orderList}>
                {paginatedExecutedOrders.map((order) => {
                  const cl = order.completionChecklist;
                  const matParts: string[] = [];
                  let totalQty = 0;
                  if (cl?.materiaisUtilizados?.length) {
                    cl.materiaisUtilizados.forEach((mat) => {
                      const qty = cl.materiaisQtd?.[mat] ?? 1;
                      matParts.push(`${mat} (${qty})`);
                      totalQty += qty;
                    });
                  }

                  const infoParts: string[] = [];
                  if (order.poleBusbar) infoParts.push(`Barramento: ${order.poleBusbar}`);
                  if (order.neighborhood) infoParts.push(`Bairro: ${order.neighborhood}`);
                  const infoLine = infoParts.join(' | ');

                  const tipoParts: string[] = [];
                  if (order.origin === 'PORTAL_PUBLICO') tipoParts.push('Público');
                  if (order.cadastroCelpe) tipoParts.push('Cadastro Celpe');
                  if (order.medida) tipoParts.push('Medida');
                  if (order.estimada) tipoParts.push('Estimada');
                  const tipoLine = tipoParts.join(', ');

                  return (
                    <div key={order.id} className={styles.orderCard}>
                      <div className={styles.orderMainRow}>
                        <span className={styles.orderProtocol}>{order.protocolNumber ?? '-'}</span>
                        <span className={styles.orderAddress}>{order.address}</span>
                        <span className={styles.orderDate}>{formatDateTime(order.updatedAt)}</span>
                      </div>
                      {infoLine && (
                        <div className={styles.orderInfoRow}>
                          {infoLine}
                        </div>
                      )}
                      {order.latitude != null && order.longitude != null && (
                        <div className={styles.orderInfoRow}>
                          📌{' '}
                          <a
                            href={`https://maps.google.com/?q=${order.latitude},${order.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
                          >
                            {order.latitude.toFixed(6)}, {order.longitude.toFixed(6)}
                          </a>
                        </div>
                      )}
                      {tipoLine && (
                        <div className={styles.orderInfoRow}>
                          <strong>Tipo:</strong> {tipoLine}
                        </div>
                      )}
                      {cl && (
                        <div className={styles.orderInfoRow}>
                          {cl.lampadaEncontrada && cl.lampadaEncontrada !== 'NENHUMA' && (
                            <>Encontrada: <strong>{cl.lampadaEncontrada}</strong>{' '}</>
                          )}
                          {cl.lampadaInstalada && cl.lampadaInstalada !== 'NENHUMA' && (
                            <>Instalada: <strong>{cl.lampadaInstalada}</strong>{' '}</>
                          )}
                          {cl.refletorInstalado && cl.refletorInstalado !== 'NENHUMA' && (
                            <>Refletor: <strong>{cl.refletorInstalado}</strong></>
                          )}
                        </div>
                      )}
                      {matParts.length > 0 && (
                        <div className={styles.orderMaterialsRow}>
                          <strong>{totalQty} itens</strong> &mdash; {matParts.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {executedOrders.length > ITEMS_PER_PAGE && (
              <div className={styles.pagination}>
                <span className={styles.paginationInfo}>
                  Mostrando {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, executedOrders.length)} de {executedOrders.length}
                </span>
                <div className={styles.paginationControls}>
                  <button
                    className={styles.paginationButton}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={safeCurrentPage === 1}
                  >
                    Anterior
                  </button>
                  {visiblePages.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      className={`${styles.paginationButton} ${pageNumber === safeCurrentPage ? styles.paginationButtonActive : ''}`}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </button>
                  ))}
                  <button
                    className={styles.paginationButton}
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={safeCurrentPage === totalPages}
                  >
                    Próxima
                  </button>
                </div>
                <div className={styles.paginationJump}>
                  <span className={styles.paginationJumpLabel}>Ir para a página</span>
                  <input
                    type="number"
                    min={1}
                    max={totalPages}
                    className={styles.paginationJumpInput}
                    value={pageInput}
                    onChange={(event) => setPageInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') goToPage();
                    }}
                    placeholder={`1-${totalPages}`}
                  />
                  <button
                    type="button"
                    className={styles.paginationJumpButton}
                    onClick={goToPage}
                  >
                    Ir
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Relatorios;